// ============================================================
// PlateObject - an Object's plate (2026-10-26, boards 5 and 7 of the Cover & Objects canvas).
//
// The diamond in its team's colour, resting on its ring; the ring is a gauge in the health ramp's colour that
// drains with its Wounds (stepped by band when the table plays without numbers), and the damage is on the body:
// cracks when damaged, more when critical, shards on the ground and a dashed ring once destroyed. Under it, what it is
// (armour, Toughness Bonus, its box) and what it is not (no dodge or parry, no conditions, no turn).
//
// It keeps the plates' contract: `data-figure`, `data-band`, a right-click on the Structure for the armour edit.
// It plays no hit of its own yet: the burst marks are a person's and a hull's.
// ============================================================
import { useContext, useMemo } from 'react';
import type { CSSProperties } from 'react';
import clsx from '../../utils/clsx';
import RosterContext from '../../contexts/RosterContext';
import { liveryByCombatant, liveryColour } from '../../engine/roster/team';
import { cracksFor, diamond, gaugeArc, objectState, shardsAt, type ObjectState } from '../../engine/plate/objectPlate';
import { plateBand } from '../../engine/plate/plateBand';
import { healthGauge, vitalsOf } from '../../engine/healthRamp';
import { readObjectSpec } from '../../engine/objects/objectSpec';
import { DEFAULT_MATERIALS } from '../../engine/battlefield/voxels';
import { useWoundDisplay } from '../../hooks/useWoundDisplay';
import type { Character } from '../../engine/character/types';
import type { Combatant } from '../../engine/roster/types';
import type { Target } from '../../types';
import { PlateHit, plateSize } from './plateParts';
import plates from '../../styles/BodyPlate.module.css';
import styles from '../../styles/PlateObject.module.css';

interface Props {
  target: Target;
  /** The combatant's sheet: its box and material. */
  sheet?: Character | null;
  /** Whose object: its team's livery colours the diamond. */
  combatantId?: string | null;
  side?: 'target' | 'actor';
  fill?: boolean;
  onLocationContextMenu?: (id: string) => void;
}

const VB = { w: 400, h: 430 };
const BODY = { cx: 200, cy: 175, w: 150, h: 220 };
const RING = { cx: 200, cy: 318, rx: 112, ry: 26 };
/** The Structure's hit area: the diamond's outline. */
const HIT_PATH = [
  `M${BODY.cx} ${BODY.cy - BODY.h / 2}`,
  `L${BODY.cx + BODY.w / 2} ${BODY.cy}`,
  `L${BODY.cx} ${BODY.cy + BODY.h / 2}`,
  `L${BODY.cx - BODY.w / 2} ${BODY.cy} Z`,
].join(' ');
// Two lines: in one, the plate's narrow frame clipped both ends.
const NOT_A_FIGHTER = ['Cannot Dodge or Parry · no conditions', 'no turn · destroyed past 0'];

/** Its team's colour, or the no-team livery; read from the roster when there is one around it. */
function useObjectLivery(combatantId: string | null | undefined): string {
  const roster = (useContext(RosterContext) as { roster?: Combatant[] } | null)?.roster;
  const liveries = useMemo(() => (roster ? liveryByCombatant(roster) : {}), [roster]);
  const livery = combatantId ? liveries[combatantId] : undefined;
  return livery ? liveryColour(livery) : 'var(--livery-none)';
}

/** "Armour 8 · TB 4 · Stone box", or "· mark only" for an object that is not terrain. */
function factsOf(target: Target, sheet: Character | null | undefined): string {
  const spec = readObjectSpec(sheet?.objectSpec);
  const body = spec.terrain ? `${DEFAULT_MATERIALS[spec.material]?.name ?? 'Stone'} box` : 'mark only';
  return `Armour ${target.armour?.structure ?? 0} · TB ${target.toughnessBonus ?? 0} · ${body}`;
}

function Body({ state }: { state: ObjectState }) {
  const { cx, cy, w, h } = BODY;
  if (state === 'destroyed') {
    return (
      <g className={styles.shards} data-testid="object-shards">
        {shardsAt(cx, RING.cy - 6, w * 1.4).map((points) => (
          <polygon key={points} points={points} />
        ))}
      </g>
    );
  }
  const d = diamond(cx, cy, w, h);
  return (
    <g className={styles.body}>
      {d.faces.map((f) => (
        <polygon key={f.id} className={styles[f.id]} points={f.points} />
      ))}
      <line className={styles.equator} x1={d.equator[0]} y1={d.equator[1]} x2={d.equator[2]} y2={d.equator[3]} />
      {cracksFor(state, cx, cy, w, h).map((points) => (
        <polyline key={points} className={styles.crack} points={points} />
      ))}
    </g>
  );
}

function Ring({ fill, destroyed }: { fill: number; destroyed: boolean }) {
  const { cx, cy, rx, ry } = RING;
  return (
    <g data-testid="object-ring" data-fill={fill}>
      <ellipse className={clsx(styles.ring, destroyed && styles.ringDestroyed)} cx={cx} cy={cy} rx={rx} ry={ry} />
      {!destroyed && <path className={styles.gauge} d={gaugeArc(cx, cy, rx, ry, fill)} />}
    </g>
  );
}

export default function PlateObject({ target, sheet, combatantId, side = 'target', fill = false, ...rest }: Props) {
  const { showNumbers } = useWoundDisplay();
  const state = objectState(target);
  const { shown, total } = vitalsOf(target);
  const gauge = healthGauge(shown, total, showNumbers);
  const livery = useObjectLivery(combatantId);
  return (
    <svg
      viewBox={`0 0 ${VB.w} ${VB.h}`}
      className={clsx(plateSize(fill), plates.plate, side === 'actor' && plates.actor, styles.object)}
      style={{ '--object-livery': livery } as CSSProperties}
      data-band={plateBand(target, state === 'destroyed')}
      data-figure="object"
      data-state={state}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="object body hit location map"
    >
      <Ring fill={gauge} destroyed={state === 'destroyed'} />
      <Body state={state} />
      <PlateHit id="structure" d={HIT_PATH} onContextMenu={rest.onLocationContextMenu} />
      <text className={styles.facts} x={VB.w / 2} y={378} data-testid="object-facts">
        {state === 'destroyed' ? 'DESTROYED' : factsOf(target, sheet)}
      </text>
      {NOT_A_FIGHTER.map((line, i) => (
        <text key={line} className={styles.line} x={VB.w / 2} y={400 + i * 16}>
          {line}
        </text>
      ))}
    </svg>
  );
}

/** The diamond alone, for a thumbnail or a gallery card: in its livery, no ring, no words. */
export function ObjectThumb({
  className,
  ariaLabel,
  opacity,
}: {
  className?: string;
  ariaLabel?: string;
  opacity?: number;
}) {
  const d = diamond(50, 50, 56, 84);
  return (
    <svg
      viewBox="0 0 100 100"
      className={clsx(className, styles.thumb)}
      role="img"
      aria-label={ariaLabel ?? 'Object'}
      opacity={opacity}
    >
      {d.faces.map((f) => (
        <polygon key={f.id} className={styles[f.id]} points={f.points} />
      ))}
    </svg>
  );
}
